import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveDirective as _resolveDirective, withDirectives as _withDirectives, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "text-xl font-semibold text-gray-800" }
const _hoisted_2 = { class: "flex items-center space-x-4" }
const _hoisted_3 = { class: "gap-4 md:grid md:grid-cols-8" }
const _hoisted_4 = {
  key: 0,
  class: "flex flex-col gap-6 md:flex-row"
}
const _hoisted_5 = { class: "flex-grow" }
const _hoisted_6 = {
  for: "media",
  class: "mb-2 block text-sm font-medium leading-7 text-gray-900"
}
const _hoisted_7 = { class: "flex-grow" }
const _hoisted_8 = { class: "flex-grow" }
const _hoisted_9 = {
  key: 0,
  class: "flex items-end"
}
const _hoisted_10 = { class: "sr-only" }
const _hoisted_11 = { class: "flex justify-end px-4 sm:px-0" }

import { AxiosResponse } from 'axios';
import useVuelidate, { Validation, ValidationArgs } from '@vuelidate/core';
import { maxLength, minLength, required } from '@vuelidate/validators';
import { computed, onMounted, reactive, Ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { toast, toastServiceError } from '@/services';
import { useUiStore } from '@/stores';
import { TrashIcon } from '@heroicons/vue/outline';
import ScriptService from '@/services/api/ScriptService';
import {
    FormAction,
    MultiSelectOption,
    Permission,
    ScriptFormState,
    ScriptNumberFormState,
} from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'ScriptFormPage',
  props: {
    action: { type: String, default: FormAction.CREATE },
},
  setup(__props) {

const { t } = useI18n();
const uiStore = useUiStore();
const route = useRoute();
const router = useRouter();

const props = __props;

const mediaOptions = [
    {
        label: t('script.edit.form.media.options.direto'),
        value: 'direto',
    },
    {
        label: t('script.edit.form.media.options.facebook'),
        value: 'facebook',
    },
    {
        label: t('script.edit.form.media.options.gclid'),
        value: 'gclid',
    },
    {
        label: t('script.edit.form.media.options.organico'),
        value: 'organico',
    },
] as MultiSelectOption[];

const defaultMediaOption = mediaOptions.findIndex((option) => option.value === 'gclid');
const formState = reactive<ScriptFormState>({
    clientUrl: '',
    isActive: true,
    includeJquery: false,
    numbers: [
        {
            media: mediaOptions[defaultMediaOption],
            fromNumber: '',
            toNumber: '',
        },
    ],
});

const rules = computed<ValidationArgs<ScriptFormState>>(() => ({
    clientUrl: { required, maxLength: maxLength(255) },
    isActive: { required },
    includeJquery: { required },
    numbers: { required, minLength: minLength(1) },
}));

const v$: Ref<Validation<ValidationArgs<ScriptFormState>>> = useVuelidate(rules, formState);

const numberRules: ValidationArgs<ScriptNumberFormState> = {
    media: { required, maxLength: maxLength(250) },
    fromNumber: { required, maxLength: maxLength(20) },
    toNumber: { required, maxLength: maxLength(20) },
};

let vv$ = formState.numbers.map((number) => useVuelidate(numberRules, number));

watch(
    () => formState.numbers,
    () => {
        vv$ = formState.numbers.map((number) => useVuelidate(numberRules, number));
    },
    { deep: true },
);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchScriptById = async (id: number) => {
    try {
        setLoading(true);
        const { data } = await ScriptService.get<ScriptFormState>(id);
        const formData = {
            ...data,
            numbers: data.numbers.map((number) => ({
                ...number,
                media: mediaOptions.find((option) => option.value === number.media),
            })),
        };
        Object.assign(formState, formData);
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

onMounted(async () => {
    if (props.action === FormAction.EDIT) {
        const scriptId = Number(route.params.id);
        if (!isNaN(scriptId)) fetchScriptById(scriptId);
    }
});

const areAllValidationsCorrect = async (
    validationsArray: Ref<Validation<ValidationArgs<ScriptNumberFormState>>>[],
): Promise<boolean> => {
    const validationPromises = validationsArray.map((validation) => validation.value.$validate());
    await Promise.all(validationPromises);
    return validationsArray.every((validation) => validation.value.$invalid === false);
};

const removeNumber = (index: number) => {
    formState.numbers.splice(index, 1);
};

const addNumber = () => {
    formState.numbers.push({
        media: mediaOptions[0],
        fromNumber: '',
        toNumber: '',
    });
};

const onSubmit = async () => {
    const isFormCorrect = await v$.value.$validate();
    const isNumberFormCorrect = await areAllValidationsCorrect(vv$);

    if (!isFormCorrect || !isNumberFormCorrect) return;

    try {
        await setLoading(true);
        let response: AxiosResponse;

        const payload = {
            ...formState,
            numbers: formState.numbers.map((number) => ({
                ...number,
                media: number.media.value,
            })),
        };

        const handleServiceSuccess = (message: string, id: number | undefined) => {
            toast.success(message);
            v$.value.$reset();
            router.push({
                name: 'ViewScriptById',
                params: { id },
            });
        };

        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            response = await ScriptService.update(id, payload);
            handleServiceSuccess(response.data.message, id);
        } else {
            response = await ScriptService.create(payload);
            handleServiceSuccess(response.data.message, response.data.result.id);
        }
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

return (_ctx: any,_cache: any) => {
  const _component_UiBackButton = _resolveComponent("UiBackButton")!
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiTextInput = _resolveComponent("UiTextInput")!
  const _component_UiCheckboxInput = _resolveComponent("UiCheckboxInput")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_UiMultiselect = _resolveComponent("UiMultiselect")!
  const _directive_tippy = _resolveDirective("tippy")!

  return (_openBlock(), _createElementBlock("form", {
    class: "space-y-4 p-4 sm:p-6 lg:p-8",
    autocomplete: "off",
    onSubmit: _withModifiers(onSubmit, ["prevent"])
  }, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_1, _toDisplayString(_ctx.$t('script.' + props.action + '.title')), 1)
      ]),
      actions: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_UiBackButton, { fallbackTo: { name: 'ListScript' } }),
          (
                            (props.action == _unref(FormAction).CREATE && _ctx.$can(_unref(Permission).CREATE_SCRIPT)) ||
                            (props.action == _unref(FormAction).EDIT && _ctx.$can(_unref(Permission).EDIT_SCRIPT))
                        )
            ? (_openBlock(), _createBlock(_component_UiButton, {
                key: 0,
                type: "submit",
                variant: "primary",
                "text-variant": "white"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Save')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiPanel, { class: "space-y-4" }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_component_UiTextInput, {
            modelValue: _unref(v$).clientUrl.$model,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(v$).clientUrl.$model) = $event)),
            name: "clientUrl",
            type: "text",
            label: _ctx.$t('script.labels.clientUrl'),
            errors: _unref(v$).clientUrl.$errors,
            required: _unref(v$).clientUrl.required !== undefined,
            disabled: props.action == _unref(FormAction).EDIT,
            class: "md:col-span-6 lg:col-span-4",
            onBlur: _unref(v$).clientUrl.$touch
          }, null, 8, ["modelValue", "label", "errors", "required", "disabled", "onBlur"]),
          _createVNode(_component_UiCheckboxInput, {
            modelValue: formState.includeJquery,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formState.includeJquery) = $event)),
            name: "includeJquery",
            label: _ctx.$t('script.labels.includeJquery'),
            class: "col-span-2"
          }, null, 8, ["modelValue", "label"]),
          _createVNode(_component_UiCheckboxInput, {
            modelValue: formState.isActive,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formState.isActive) = $event)),
            name: "isActive",
            label: _ctx.$t('script.labels.isActive'),
            class: "col-span-2"
          }, null, 8, ["modelValue", "label"])
        ])
      ]),
      _: 1
    }),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(formState.numbers, (number, index) => {
      return (_openBlock(), _createElementBlock("div", { key: index }, [
        _createVNode(_component_UiPanel, null, {
          default: _withCtx(() => [
            (_unref(vv$)[index])
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("label", _hoisted_6, _toDisplayString(_unref(t)('script.labels.media')), 1),
                    _createVNode(_component_UiMultiselect, {
                      modelValue: number.media,
                      "onUpdate:modelValue": ($event: any) => ((number.media) = $event),
                      name: "media",
                      label: "label",
                      "value-prop": "value",
                      options: mediaOptions,
                      errors: _unref(vv$)[index].value.media.$errors,
                      onBlur: _unref(vv$)[index].value.media.$touch
                    }, null, 8, ["modelValue", "onUpdate:modelValue", "errors", "onBlur"])
                  ]),
                  _createElementVNode("div", _hoisted_7, [
                    _createVNode(_component_UiTextInput, {
                      modelValue: number.fromNumber,
                      "onUpdate:modelValue": ($event: any) => ((number.fromNumber) = $event),
                      name: "fromNumber",
                      type: "text",
                      label: _unref(t)('script.labels.fromNumber'),
                      errors: _unref(vv$)[index].value.fromNumber.$errors,
                      onBlur: _unref(vv$)[index].value.fromNumber.$touch
                    }, null, 8, ["modelValue", "onUpdate:modelValue", "label", "errors", "onBlur"])
                  ]),
                  _createElementVNode("div", _hoisted_8, [
                    _createVNode(_component_UiTextInput, {
                      modelValue: number.toNumber,
                      "onUpdate:modelValue": ($event: any) => ((number.toNumber) = $event),
                      name: "toNumber",
                      type: "text",
                      label: _unref(t)('script.labels.toNumber'),
                      errors: _unref(vv$)[index].value.toNumber.$errors,
                      onBlur: _unref(vv$)[index].value.toNumber.$touch
                    }, null, 8, ["modelValue", "onUpdate:modelValue", "label", "errors", "onBlur"])
                  ]),
                  (formState.numbers.length > 1)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                        _withDirectives((_openBlock(), _createBlock(_component_UiButton, {
                          size: "",
                          shape: "rounded-full",
                          variant: "inherit",
                          "text-variant": "danger",
                          shadow: "none",
                          themed: "",
                          class: "focus:ring-danger ml-1 border-transparent text-sm shadow-none",
                          onClick: ($event: any) => (removeNumber(index))
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("span", _hoisted_10, _toDisplayString(_unref(t)('core.actions.Remove')), 1),
                            _createVNode(_unref(TrashIcon), {
                              class: "h-6 w-6",
                              "aria-hidden": "true"
                            })
                          ]),
                          _: 2
                        }, 1032, ["onClick"])), [
                          [_directive_tippy, _unref(t)('core.actions.Remove')]
                        ])
                      ]))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 2
        }, 1024)
      ]))
    }), 128)),
    _createElementVNode("div", _hoisted_11, [
      _createVNode(_component_UiButton, {
        variant: "white",
        "text-variant": "gray-500",
        class: "ml-4",
        onClick: addNumber
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t('script.actions.addNumber')), 1)
        ]),
        _: 1
      })
    ])
  ], 32))
}
}

})